import { BrowserRouting } from '@aipb/gulliver/browser/routing';
import { FieldRepository, FieldService } from '@aipb/gulliver/castor/data-access';
import { SearchHighlighting, SearchSort } from '@aipb/gulliver/castor/model';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { CatalogRecHeader, CatalogRecHeaderImage, CustomRecordItem } from '@aipb/gulliver/catalog/model';
import { CatalogRouting } from '@aipb/gulliver/catalog/routing';
import { createDetailCompositeKey, isDropbox } from '@aipb/gulliver/catalog/util';
import { DropboxRepository } from '@aipb/gulliver/dropbox/data-access';
import { EnvService } from '@aipb/gulliver/env/util';
import { PersonalContentPermissionService } from '@aipb/gulliver/personal-content/auth';
import { AddToFavoritesButtonComponent } from '@aipb/gulliver/personal-content/feature-favorite-button';
import { AddToFavoritesButtonConfig } from '@aipb/gulliver/personal-content/model';
import { ResolverService } from '@aipb/gulliver/resolver';
import { openUrl } from '@aipb/shared/util/routing';
import { SnackbarService } from '@aipb/shared/util/snackbar';
import { PinRecordUseCase } from '@aipb/usecase';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CompareDialogService } from '../../dialogs';
import { HeaderContentComponent } from '../header-content/header-content.component';
import { HeaderHighlightingComponent } from '../header-highlighting/header-highlighting.component';
import { HeaderImageComponent } from '../header-image/header-image.component';
import { HeaderMenuComponent } from '../header-menu/header-menu.component';
import { MatIconModule } from '@angular/material/icon';

@UntilDestroy()
@Component({
  selector: 'cat-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AddToFavoritesButtonComponent,
    HeaderImageComponent,
    MatButtonModule,
    MatMenuModule,
    HeaderHighlightingComponent,
    HeaderMenuComponent,
    HeaderContentComponent,
    MatIconModule
  ]
})
export class HeaderComponent implements OnInit {
  @Input() coreId: string;
  @Input() item: CustomRecordItem;
  @Input() header: CatalogRecHeader;
  @Input() enablePin = true;
  @Input() enableMenu = true;
  @Input() enableFullscreen = false;
  @Input() highlighting: SearchHighlighting = {};
  @Input() compact = false;
  @Input() enableOverlay = false;

  @Output() action = new EventEmitter<CustomRecordItem>();
  @Output() fullscreen = new EventEmitter();
  @Output() openOverlay = new EventEmitter();

  private browserRouting = inject(BrowserRouting);
  private catalogRouting = inject(CatalogRouting);
  private snackbar = inject(SnackbarService);
  private catalogEnv = inject(CatalogEnvService);
  private fieldService = inject(FieldService);
  private transloco = inject(TranslocoService);
  private compareDialogService = inject(CompareDialogService);
  private personalContentPermission = inject(PersonalContentPermissionService);
  private dropboxRepository = inject(DropboxRepository);
  private env = inject(EnvService);
  private resolverService = inject(ResolverService);
  private fieldRepository = inject(FieldRepository);
  private document = inject(DOCUMENT);
  private pinRecordUseCase = inject(PinRecordUseCase);

  private _sort: SearchSort[] = [];

  get sort() {
    return this._sort;
  }

  @Input() set sort(value: SearchSort[]) {
    this._sort = value;
    this.initSortString();
  }

  private _sortString: string;

  get sortString(): string {
    return this._sortString;
  }

  get enableHighlighting() {
    return this.catalogEnv.env.enableHighlighting;
  }

  get headerCollapsible() {
    return this.catalogEnv.env.headerCollapsible;
  }

  get showHighlighting(): boolean {
    return !!this.catalogEnv.env?.fulltext?.showHighlighting;
  }

  get hasAddToFavoritesButton(): boolean {
    return this.personalContentPermission.canUsePersonalContent && this.env.isPersonalContentEnabled;
  }

  get hasBrowser(): boolean {
    return this.env.hasBrowser;
  }

  get isFullscreen(): boolean {
    return !!this.document.fullscreenElement;
  }

  get addToFavoritesButtonConfig(): AddToFavoritesButtonConfig {
    const params: AddToFavoritesButtonConfig = {
      type: isDropbox(this.item.GulliverRecType) ? 'RECDBOX' : 'REC',
      service: this.catalogEnv.env.service,
      serviceValue: createDetailCompositeKey(this.coreId, this.item.header.id)
    };

    if (isDropbox(this.item.GulliverRecType)) {
      params['serviceLabel'] = this.header.label.value;
    } else {
      params['userLabel'] = this.header.label.value;
      params['userThumb'] = this.header.images && this.header.images.length > 0 ? this.header.images[0].thumb : null;
    }

    return params;
  }

  get hideSortInHeader(): boolean {
    return this.catalogEnv.env.hideSortInHeader;
  }

  get id() {
    return createDetailCompositeKey(this.coreId, this.item[this.catalogEnv.env.castor.UUID]);
  }

  ngOnInit() {
    this.fieldService.onSetup$.pipe(untilDestroyed(this)).subscribe(() => {
      this.initSortString();
    });
  }

  selectImageUrl(image: CatalogRecHeaderImage): Observable<string> {
    if (isDropbox(this.item.GulliverRecType)) {
      return this.dropboxRepository
        .selectImage(this.item[this.catalogEnv.env.castor.UUID])
        .pipe(map((image) => `data:image/png;base64,${image}`));
    }

    return of(image ? image.thumb || image.large : null);
  }

  onCopyUrl() {
    const url = this.catalogRouting.detailSingleAbsUrl(this.id);

    this.resolverService.copyUrl(url).subscribe(() => {
      this.snackbar.open(this.transloco.translate('catalog.messages.detail_url_copied'), 'success');
    });
  }

  onPin(): void {
    this.pinRecordUseCase.execute({
      id: this.id,
      label: this.item.GulliverTabName
    });
  }

  onCompare(): void {
    this.compareDialogService.open({
      id: this.id
    });

    /*this.catalogDialog.compareList({
      id: this.id
    });*/
  }

  onOpenInBrowser(): void {
    const url = this.browserRouting.galleryDefaultAbsUrl(this.item.fetch[0].url);
    openUrl(url);
  }

  private initSortString(): void {
    this._sortString = this._sort
      .map((s) => {
        const sortField = this.fieldRepository.getSortField(this.coreId, s.field);
        if (!(s.field in this.item)) {
          return `${sortField ? sortField.fieldName : '<UNKNOWN>'}: ${this.transloco.translate(
            'catalog.messages.sort_field_not_available'
          )}`;
        }

        return `${sortField ? sortField.fieldName : '<UNKNOWN>'}: ${this.item[s.field]}`;
      })
      .join(', ');
  }
}
